import type { ReactElement } from 'react';
import styles from './PluginLoadingScreen.module.css';

export type PluginLoadingPhase =
  | { kind: 'loading'; attempt: number }
  | { kind: 'waiting'; attempt: number; secondsRemaining: number };

export interface PluginLoadingScreenProps {
  phase: PluginLoadingPhase;
}

function loadingLabel(attempt: number): string {
  return attempt >= 3 ? 'Carregando ativação... Última tentativa.' : 'Carregando ativação... Aguarde.';
}

/**
 * Cobre os estados 1 ("Carregando Plugin") e 3 ("Erro com retry automático
 * em andamento") do Design da spec (HUB-237, CA-06/07/08) — reusado tanto
 * na resolução do registro (`usePluginRegistryResolution`, CA-16) quanto no
 * carregamento via Module Federation (`usePluginLoadRetry`), que
 * compartilham a mesma linguagem visual.
 *
 * Ícone estático (fase de espera) vs. spinner animado (fase de
 * carregamento) — distinção visual clara em ambiente de luz solar direta
 * (Design > Acessibilidade e hardware de agência). Countdown visual é
 * obrigatório na fase de espera: sem ele, o operador de estande não
 * distingue "travado" de "retentando".
 */
export function PluginLoadingScreen({ phase }: PluginLoadingScreenProps): ReactElement {
  if (phase.kind === 'waiting') {
    return (
      <main className={styles.container} aria-label="Carregando ativação" role="main">
        <div className={styles.waitingBanner} role="status" aria-live="polite">
          <span className={styles.warningIcon} aria-hidden="true">
            ⚠️
          </span>
          <p className={styles.waitingText}>
            Falha ao carregar. Tentando novamente em {phase.secondsRemaining}s...
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.container} aria-label="Carregando ativação" role="main">
      <div className={styles.content}>
        <div className={styles.spinnerArea} aria-hidden="true">
          <div className={styles.spinner} />
        </div>
        <p className={styles.loadingText} role="status" aria-live="polite" aria-label={loadingLabel(phase.attempt)}>
          {loadingLabel(phase.attempt)}
        </p>
      </div>
    </main>
  );
}
